import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  menuList,
  roleMenuList,
  updateRoleMenuList,
  userMenuList,
  updateUserMenuList,
} from "../../../services/menu";
import { rolelist, userListByRole, branchlist } from "../../../services/master";

// Collect all menu ids from a (possibly nested) menu tree
function collectIds(items, acc = []) {
  (items || []).forEach((it) => {
    acc.push(it.id);
    if (it.children) collectIds(it.children, acc);
  });
  return acc;
}

const MenuPermission = () => {
  const [roles, setRoles] = useState([]);
  const [roleId, setRoleId] = useState("");
  const [branches, setBranches] = useState([]); // all branches
  const [branchId, setBranchId] = useState(""); // optional: narrow users by branch
  const [users, setUsers] = useState([]);     // users belonging to the chosen role/branch
  const [userId, setUserId] = useState("");   // optional: a single user to override
  const [menus, setMenus] = useState([]);     // full menu tree
  const [selected, setSelected] = useState([]); // selected ids
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    rolelist().then((res) => {
      if (res?.status === true) setRoles(res.data || []);
    });
    branchlist().then((res) => {
      if (res?.status === true) setBranches(res.data || []);
    });
    menuList().then((res) => {
      if (res?.status === true) setMenus(res.data?.Items || []);
    });
  }, []);

  // Load the users for the current role + branch selection
  const loadUsers = (role, branch) =>
    userListByRole(role, branch).then((res) => {
      if (res?.status === true) setUsers(res.data || []);
    });

  // Load a role's saved menus into the checkboxes (used as the baseline)
  const loadRoleMenus = (id) =>
    roleMenuList(id).then((res) => {
      if (res?.status === true) setSelected(collectIds(res.data?.Items || []));
    });

  const onRoleChange = (e) => {
    const id = e.target.value;
    setRoleId(id);
    setBranchId("");
    setUserId("");
    setUsers([]);
    setSelected([]);
    if (!id) return;
    loadRoleMenus(id);
    loadUsers(id, "");
  };

  const onBranchChange = (e) => {
    const branch = e.target.value;
    setBranchId(branch);
    // reset the user selection back to whole-role and reload the user list
    setUserId("");
    loadRoleMenus(roleId);
    loadUsers(roleId, branch);
  };

  const onUserChange = (e) => {
    const id = e.target.value;
    setUserId(id);
    if (!id) {
      // back to whole-role editing: show the role's saved menus
      loadRoleMenus(roleId);
      return;
    }
    // show this user's override if they have one, otherwise start from the
    // role's menus as a template the admin can tweak.
    userMenuList(id).then((res) => {
      const items = res?.status === true ? res.data?.Items || [] : [];
      if (items.length) setSelected(collectIds(items));
      else loadRoleMenus(roleId);
    });
  };

  const isChecked = (id) => selected.includes(id);

  // toggle a parent (and all its children) on/off together
  const toggleParent = (parent) => {
    const ids = collectIds([parent]);
    setSelected((prev) =>
      ids.every((i) => prev.includes(i))
        ? prev.filter((i) => !ids.includes(i))
        : Array.from(new Set([...prev, ...ids]))
    );
  };

  // toggle a single child; auto-add/remove its parent
  const toggleChild = (parent, childId) => {
    setSelected((prev) => {
      let next = prev.includes(childId)
        ? prev.filter((i) => i !== childId)
        : [...prev, childId];
      const childIds = (parent.children || []).map((c) => c.id);
      const anyChild = childIds.some((i) => next.includes(i));
      if (anyChild && !next.includes(parent.id)) next = [...next, parent.id];
      if (!anyChild) next = next.filter((i) => i !== parent.id);
      return next;
    });
  };

  const save = () => {
    if (!roleId) {
      toast.error("Please select a role");
      return;
    }
    setSaving(true);
    // A user is selected -> save a per-user override; otherwise save the role.
    const request = userId
      ? updateUserMenuList(userId, selected)
      : updateRoleMenuList(roleId, selected);
    request.then((res) => {
      setSaving(false);
      if (res?.status === true) toast.success(res.message || "Permissions updated");
    });
  };

  return (
    <div>
      <div className="container-fluid">
        <div className="page-header">
          <h2 className="main-content-title tx-24 mg-b-5">Role Permission
          </h2>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a>Settings</a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Role Permission
            </li>
          </ol>
        </div>
        <div className="content-body">
          <div className="card custom-card">
            <div className="card-body">
              <div className="row mb-4">
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Role</label>
                  <select className="form-control" value={roleId} onChange={onRoleChange}>
                    <option value="">Select Role</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.role_name || r.name || r.title || `Role ${r.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                {roleId && (
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">Branch</label>
                    <select className="form-control" value={branchId} onChange={onBranchChange}>
                      <option value="">All branches</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.branch_name || `Branch ${b.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {roleId && (
                  <div className="col-md-4">
                    <label className="form-label fw-semibold">
                      User
                      {/* <span className="text-muted fw-normal">(optional)</span> */}
                    </label>
                    <select className="form-control" value={userId} onChange={onUserChange}>
                      <option value="">Whole role (all users)</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name || u.username || `User ${u.id}`}
                        </option>
                      ))}
                    </select>
                    {/* <small className="text-muted">
                      {userId
                        ? "Saving will override this user's menus only."
                        : "Leave empty to set permissions for the entire role."}
                    </small> */}
                  </div>
                )}
              </div>

              {roleId && (
                <>
                  <div className="row">
                    {menus.map((parent) => (
                      <div className="col-md-4 mb-4" key={parent.id}>
                        <div className="border rounded p-3 h-100">
                          <label className="d-flex align-items-center fw-bold mb-2" style={{ cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              className="me-2"
                              checked={isChecked(parent.id)}
                              onChange={() => toggleParent(parent)}
                            />
                            {parent.icon && <i className={`${parent.icon} me-2`} />}
                            {parent.title}
                          </label>
                          {parent.children && (
                            <div className="ms-4">
                              {parent.children.map((child) => (
                                <label
                                  key={child.id}
                                  className="d-flex align-items-center mb-1"
                                  style={{ cursor: "pointer" }}
                                >
                                  <input
                                    type="checkbox"
                                    className="me-2"
                                    checked={isChecked(child.id)}
                                    onChange={() => toggleChild(parent, child.id)}
                                  />
                                  {child.title}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <button className="btn btn-primary" onClick={save} disabled={saving}>
                    {saving
                      ? "Saving…"
                      : userId
                      ? "Save User Permissions"
                      : "Save Role Permissions"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

      </div>

    </div>
  );
};

export default MenuPermission;
